'use client'

import { type ComponentType, useEffect, useRef, useState } from 'react'

// Lazy boundary for the wizard. `next/dynamic` (its previous form) still
// preloads the chunk during hydration, so on a page that renders a wizard the
// ~52KB multi-step machine landed in the initial JS parade and competed with the
// render-blocking CSS for bandwidth (see
// docs/decisions/2026-06-28-storyblok-scoped-component-maps.md → Tier 2). The
// wizard is interaction-only and below the fold, so load it on scroll-proximity
// instead — mirrors components/client/product/related-products/related-products-lazy.
interface WizardLazyProps {
  position: string
  segmentOrigin: string
}

export const WizardLazy = (props: WizardLazyProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [shouldLoad, setShouldLoad] = useState(false)
  const [Wizard, setWizard] = useState<ComponentType<WizardLazyProps> | null>(null)

  useEffect(() => {
    if (shouldLoad) return
    const el = ref.current
    if (!el || !('IntersectionObserver' in window)) {
      setShouldLoad(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '900px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [shouldLoad])

  useEffect(() => {
    if (!shouldLoad || Wizard) return
    let cancelled = false
    void import('./wizard').then((m) => {
      if (!cancelled) setWizard(() => m.Wizard)
    })
    return () => {
      cancelled = true
    }
  }, [Wizard, shouldLoad])

  return <div ref={ref}>{Wizard ? <Wizard {...props} /> : null}</div>
}
